import { bozpHandoffControllerExchangeV1 } from '@fixit/api-client/bozp-handoff';
import {
  bozpTrainingControllerCompleteV1,
  bozpTrainingControllerGetProgressV1,
  bozpTrainingControllerGetTrainingV1,
  bozpTrainingControllerOpenSessionV1,
} from '@fixit/api-client/bozp-training';
import type { ClientQuestion, TrainingContent } from '@fixit/bozp-content';
import { PLAYER_CODE_SHA } from '@/env';

/** Shape returned by GET /bozp/training/:id (questions have `correct` stripped server-side). */
export interface TrainingPayload {
  id: string;
  slug: string;
  /**
   * Profile-level title is a PLAIN string (the `bozp_hazard_profiles.title` varchar column,
   * V1 Czech) — NOT LocalizedText. Chapter titles inside `content` stay LocalizedText and
   * are resolved with `resolveLocalized`.
   */
  title: string;
  version: number;
  versionHash: string;
  /** The published version row this content came from; sessions bind to it. */
  profileVersionId: string;
  passThreshold: number;
  /** Non-empty = theory-only course; the qualification is proven by a separate external exam. */
  requiredQualificationRefs: string[];
  /** Czech legal basis references for this profile (e.g. "NV 194/2022"). */
  legalBasisRefs: string[];
  content: TrainingContent;
  /** `correct`/`tolerance` are NOT present — grading is server-side only. */
  questions: ClientQuestion[];
}

export interface ExchangeResult {
  accessToken: string;
  expiresIn: number;
  hazardProfileId: string | null;
}

export interface CompletionResult {
  passed: boolean;
  score: number;
  completionId: string;
  /** Question ids the server graded 0 (gradeAnswer === 0). Drives chapter-level weak-topic feedback. */
  wrongQuestionIds: string[];
}

/** A server-owned training session (POST /bozp/sessions). */
export interface TrainingSession {
  sessionId: string;
  hazardProfileId: string;
  profileVersionId: string;
  versionHash: string;
  startedAt: string;
  expiresAt: string;
}

export interface ServerProgress {
  furthestChapterId: string | null;
  lastSessionId: string | null;
  completedChapterIds: string[];
}

export interface AnswerSubmission {
  questionId: string;
  choice: string | string[] | number | boolean | null;
}

/** Exchange the one-time handoff for a scoped token. orval types the body `void`; cast the JSON. */
export async function exchangeHandoff(code: string): Promise<ExchangeResult> {
  return (await bozpHandoffControllerExchangeV1({ code })) as unknown as ExchangeResult;
}

export async function getTraining(hazardProfileId: string): Promise<TrainingPayload> {
  return (await bozpTrainingControllerGetTrainingV1(hazardProfileId)) as unknown as TrainingPayload;
}

export async function openTrainingSession(hazardProfileId: string): Promise<TrainingSession> {
  return (await bozpTrainingControllerOpenSessionV1({
    hazardProfileId,
  })) as unknown as TrainingSession;
}

export async function getServerProgress(hazardProfileId: string): Promise<ServerProgress> {
  return (await bozpTrainingControllerGetProgressV1(hazardProfileId)) as unknown as ServerProgress;
}

export async function submitCompletion(input: {
  hazardProfileId: string;
  /** The server-owned session (POST /bozp/sessions) the chapters were recorded in. */
  sessionId: string;
  answers: AnswerSubmission[];
  /** Version of the SES declaration the worker confirmed before submit (the doklad signature layer). */
  acknowledgedDeclarationVersion?: string;
}): Promise<CompletionResult> {
  return (await bozpTrainingControllerCompleteV1({
    hazardProfileId: input.hazardProfileId,
    sessionId: input.sessionId,
    answers: input.answers,
    playerCodeSha: PLAYER_CODE_SHA,
    acknowledgedDeclarationVersion: input.acknowledgedDeclarationVersion,
  })) as unknown as CompletionResult;
}
